import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Database, Paperclip, Download, X, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { usePropiedad } from '../contexts/PropiedadContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Datos = () => {
  const { propiedadSeleccionada } = usePropiedad();
  const [datos, setDatos] = useState([]);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [datoEditando, setDatoEditando] = useState(null);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  const [formData, setFormData] = useState({
    propiedad_id: '',
    nombre: '',
    dato: '',
    adjunto: null,
    es_variable: true
  });

  useEffect(() => {
    if (propiedadSeleccionada) {
      cargarDatos();
    }
  }, [propiedadSeleccionada]);

  const cargarDatos = async () => {
    if (!propiedadSeleccionada) return;
    
    try {
      const response = await axios.get(`${API}/datos?propiedad_id=${propiedadSeleccionada.id_propiedad}`);
      setDatos(response.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const abrirModal = (dato = null) => {
    if (dato) {
      setDatoEditando(dato);
      setFormData({
        propiedad_id: dato.propiedad_id || propiedadSeleccionada?.id_propiedad || '',
        nombre: dato.nombre,
        dato: dato.dato,
        adjunto: dato.adjunto || null,
        es_variable: dato.es_variable !== false // Default true si no existe
      });
    } else {
      setDatoEditando(null);
      setFormData({
        propiedad_id: propiedadSeleccionada?.id_propiedad || '',
        nombre: '',
        dato: '',
        adjunto: null,
        es_variable: true
      });
    }
    setArchivoSeleccionado(null);
    setModalAbierto(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArchivoSeleccionado(file);
    }
  };

  const subirArchivo = async () => {
    if (!archivoSeleccionado) return null;
    
    try {
      setSubiendoArchivo(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', archivoSeleccionado);
      
      const response = await axios.post(`${API}/upload-dato-adjunto`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubiendoArchivo(false);
      return response.data.url;
    } catch (error) {
      setSubiendoArchivo(false);
      toast.error('Error al subir archivo');
      return null;
    }
  };

  const guardarDato = async () => {
    if (!formData.nombre.trim() || !formData.dato.trim()) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    try {
      let adjuntoUrl = formData.adjunto;
      
      if (archivoSeleccionado) {
        adjuntoUrl = await subirArchivo();
        if (!adjuntoUrl) return;
      }

      const datoData = {
        ...formData,
        adjunto: adjuntoUrl
      };

      if (datoEditando) {
        await axios.put(`${API}/datos/${datoEditando.id_dato}`, datoData);
        toast.success('Dato actualizado');
      } else {
        await axios.post(`${API}/datos`, datoData);
        toast.success('Dato creado');
      }
      
      cargarDatos();
      setModalAbierto(false);
    } catch (error) {
      toast.error('Error al guardar dato');
    }
  };

  const eliminarDato = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este dato?')) {
      try {
        await axios.delete(`${API}/datos/${id}`);
        toast.success('Dato eliminado');
        cargarDatos();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0E3A53] flex items-center gap-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <Database className="w-8 h-8 text-[#1AA1D8]" />
              Datos
            </h2>
            <p className="text-[#0E3A53]/70 mt-1">Gestiona información importante</p>
          </div>
          <Button 
            onClick={() => abrirModal()}
            className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] hover:opacity-90"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuevo Dato
          </Button>
        </div>

        <div className="grid gap-4">
          {datos.map(dato => (
            <div key={dato.id_dato} className="bg-gradient-to-r from-[#8FE0FF]/20 to-[#F7E8C9]/20 rounded-xl p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#0E3A53] text-lg">{dato.nombre}</h3>
                    {dato.es_variable !== false && (
                      <span className="px-2 py-0.5 bg-[#1AA1D8]/20 text-[#1AA1D8] text-xs rounded-full flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        Variable
                      </span>
                    )}
                  </div>
                  <p className="text-[#0E3A53]/70 mt-1">{dato.dato}</p>
                  {dato.adjunto && (
                    <a
                      href={`${BACKEND_URL}${dato.adjunto}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1AA1D8] hover:text-[#0E3A53] flex items-center gap-2 mt-2 text-sm"
                    >
                      <Paperclip className="w-4 h-4" />
                      Ver adjunto
                    </a>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => abrirModal(dato)}
                    className="p-2 hover:bg-[#1AA1D8]/20 rounded-lg text-[#1AA1D8]"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => eliminarDato(dato.id_dato)}
                    className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {datos.length === 0 && (
            <div className="text-center py-12 text-[#0E3A53]/50">
              <Database className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>No hay datos registrados</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={modalAbierto} onOpenChange={setModalAbierto}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {datoEditando ? 'Editar Dato' : 'Nuevo Dato'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Contraseña WiFi"
              />
            </div>
            <div>
              <Label htmlFor="dato">Dato *</Label>
              <Input
                id="dato"
                value={formData.dato}
                onChange={(e) => setFormData({ ...formData, dato: e.target.value })}
                placeholder="Ej: abc123456"
              />
            </div>
            <div>
              <Label htmlFor="adjunto">Adjunto (opcional)</Label>
              <div className="flex gap-2">
                <Input
                  id="adjunto"
                  type="file"
                  onChange={handleFileSelect}
                  className="flex-1"
                />
                {formData.adjunto && !archivoSeleccionado && (
                  <a
                    href={`${BACKEND_URL}${formData.adjunto}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border rounded hover:bg-gray-50"
                    title="Ver archivo actual"
                  >
                    <Download className="w-5 h-5" />
                  </a>
                )}
              </div>
              {archivoSeleccionado && (
                <p className="text-sm text-gray-600 mt-1">
                  Archivo seleccionado: {archivoSeleccionado.name}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-3 p-3 bg-[#8FE0FF]/10 rounded-lg">
              <Checkbox
                id="es_variable"
                checked={formData.es_variable}
                onCheckedChange={(checked) => setFormData({ ...formData, es_variable: checked })}
              />
              <div className="flex-1">
                <Label htmlFor="es_variable" className="cursor-pointer flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#1AA1D8]" />
                  Usar como variable en mensajes y contratos
                </Label>
                <p className="text-xs text-[#0E3A53]/60 mt-1">
                  Si está activado, aparecerá como {'{'}nombre{'}'} disponible para insertar
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setModalAbierto(false)}>
                Cancelar
              </Button>
              <Button onClick={guardarDato} disabled={subiendoArchivo}>
                {subiendoArchivo ? 'Subiendo...' : 'Guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Datos;